import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { useContext } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import * as api from '../../services/api';
import useToaster from '../useToaster';
import { QUERY_CACHE_TIME } from '../../constants';
import useAccount from './useAccount';

const useClient = ({ clientId } = {}) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const queryClient = useQueryClient();

  const storeKey = ['client', accountId, clientId];
  const clientsStoreKey = ['clients', accountId];

  const { isLoading, data } = useQuery(storeKey, () => api.fetchClient({ accountId, clientId }), {
    staleTime: QUERY_CACHE_TIME,
    cacheTime: QUERY_CACHE_TIME,
    enabled: !!clientId
  });

  const { addSuccess, addResponseError } = useToaster();

  const { mutateAsync: updateClient } = useMutation(
    (values) =>
      clientId ? api.updateClient({ accountId, clientId, ...values }) : api.addClient({ accountId, ...values }),
    {
      throwOnError: true,
      onSuccess: () => {
        queryClient.invalidateQueries(clientsStoreKey);
        queryClient.invalidateQueries(storeKey);
        addSuccess(clientId ? t('successUpdateClient') : t('successAddClient'));
      }
    }
  );

  const { mutateAsync: addClients } = useMutation(({ clients }) => api.addClients({ accountId, clients }), {
    throwOnError: true,
    onSuccess: () => {
      queryClient.invalidateQueries(clientsStoreKey);
      addSuccess(t('successAddClients'));
    },
    onError: (err) => {
      addResponseError(err);
    }
  });

  const { mutateAsync: deleteClient } = useMutation(() => api.deleteClient({ accountId, clientId }), {
    onSuccess: (_, { onSuccess }) => {
      queryClient.invalidateQueries(clientsStoreKey);
      onSuccess();
    }
  });

  return {
    loading: isLoading,
    data: data?.data,
    updateClient,
    deleteClient,
    addClients
  };
};

export default useClient;
